import { type ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import type {
  CfarContract,
  HotelItinerary,
  Reservation,
} from "@b2bportal/lodging-api";
import styles from "./TripDetailContent.module.scss";
import {
  BookingDetails,
  CheckoutBreakdown,
  HotelPolicies,
  PropertyDetails,
  TripHeader,
  TripSummaryCard,
} from ".";
import { ManageBooking } from "@/components";
import { MyTripPageHeader } from "@/overrides/apac/apac-ui/components/MyTripPageHeader/MyTripPageHeader";

interface TripDetailContentProps {
  reservation: Reservation;
  itinerary: HotelItinerary;
  isActiveBooking: boolean;
  onQueryCancellation: () => void;
  cfarContract?: CfarContract;
  isCfarExercised?: boolean;
  children?: ReactNode;
}

export const TripDetailContent = ({
  reservation,
  itinerary,
  isActiveBooking,
  onQueryCancellation,
  cfarContract,
  isCfarExercised,
  children,
}: TripDetailContentProps) => {
  const [, cn] = useModuleBEM(styles, "TripDetailContent");
  const navigate = useNavigate();

  const onCfarExercise = useCallback(() => {
    navigate("cancel-for-any-reason");
  }, [navigate]);

  return (
    <>
      <MyTripPageHeader className={cn("page-header")} />

      <div className={cn("card-wrapper")}>
        <TripHeader
          reservation={reservation}
          isCfarExercised={isCfarExercised}
        />

        <div className={cn("two-column")}>
          <TripSummaryCard
            reservation={reservation}
            hasCfar={cfarContract != null}
          />
          <ManageBooking
            className={cn("manage-booking")}
            variant={ProductType.LODGING}
            itinerary={itinerary}
            isActiveBooking={isActiveBooking}
            onQueryCancellation={onQueryCancellation}
            onCfarExercise={onCfarExercise}
          />
          <BookingDetails
            reservation={reservation}
            cfarContract={cfarContract}
          />
          <PropertyDetails reservation={reservation} />
          <HotelPolicies
            reservation={reservation}
            isActiveBooking={isActiveBooking}
            onQueryCancellation={onQueryCancellation}
            cfarContract={cfarContract}
            itinerary={itinerary}
            onCfarExercise={onCfarExercise}
          />
          <CheckoutBreakdown reservation={itinerary} />
        </div>
      </div>
      {children}
    </>
  );
};
